import React, { useEffect, useState } from "react";
import Switch from "react-switch";
import EmployeeProcessService from "../services/EmployeeProcessService";
import MenuProcessService from "../services/MenuProcessService";

function EmailRights() {
  const empcode = localStorage.getItem("empcode");
  const [employeeCode, setEmployeeCode] = useState("");
  const [emailID, setEmailID] = useState("");
  const [rights, setRights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plants, setPlants] = useState([]);
  const [plantID, setPlantID] = useState("");

  useEffect(() => {
    loadDropdowns();
  }, []);

  const loadDropdowns = async () => {
    try {
      const plantRes = await EmployeeProcessService.getPlant(empcode);
      if (plantRes?.data?.statusCode === 200) {
        setPlants(plantRes.data.result);
      }
    } catch (error) {
      console.error("Error loading dropdowns:", error);
    }
  };

  const loadAll = async () => {
    if (!plantID) {
      alert("Please select Plant first");
      return;
    }
    setLoading(true);
    try {
      const res = await MenuProcessService.getHREmail({
        action: "GetAll",
        plantID,
      });
      if (res.success) {
        setRights(res.data.result || []);
      } else {
        setRights([]);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to load email rights");
    } finally {
      setLoading(false);
    }
  };

  const searchEmployee = async () => {
    if (!plantID) {
      alert("Please select Plant first");
      return;
    }
    setLoading(true);
    try {
      const res = await MenuProcessService.getHREmail({
        action: "GetEmail",
        employeeCode,
        plantID,
      });
      if (res.success) {
        setRights(res.data.result || []);
      } else {
        setRights([]);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to load employee rights");
    } finally {
      setLoading(false);
    }
  };

  const toggleRight = (id) => {
    setRights((prev) =>
      prev.map((r) => (r.id === id ? { ...r, assign: !r.assign } : r))
    );
  };

  const saveRights = async () => {
    if (!plantID) {
      alert("Please select Plant first");
      return;
    }
    setSaving(true);
    try {
      for (const r of rights) {
        await MenuProcessService.setHREmail({
          action: "SetEmail",
          employeeCode: r.employeeCode,
          emailID: r.emailID,
          plantID,
          assign: r.assign ? 1 : 0,
        });
      }
      alert("Email rights updated successfully!");
      await loadAll();
    } catch (err) {
      console.error(err);
      alert("Failed to save rights");
    } finally {
      setSaving(false);
    }
  };

  const addEntry = async () => {
    if (!plantID || !employeeCode || !emailID) {
      alert("Plant, Employee Code and Email are required");
      return;
    }
    try {
      await MenuProcessService.setHREmail({
        action: "SetEmail",
        employeeCode,
        emailID,
        plantID,
        assign: 1,
      });
      alert("Email right added/updated successfully!");
      setEmailID("");
      await loadAll();
    } catch (err) {
      console.error(err);
      alert("Failed to add/update entry");
    }
  };

  return (
    <div className="container mt-1">
      <h3 className="mb-2 fw-bold text-primary">Email Rights Management</h3>

      {/* Row 1: Plant + Employee Code + Search + Search All */}
      <div className="card p-3 mb-3 shadow-sm">
        <div className="row g-3 align-items-end">
          <div className="col-md-3">
            <label className="form-label">
              Plant <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              value={plantID}
              onChange={(e) => setPlantID(e.target.value)}
            >
              <option value="">Select Plant</option>
              {plants.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Employee Code</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter Employee Code"
              value={employeeCode}
              onChange={(e) => setEmployeeCode(e.target.value)}
            />
          </div>

          <div className="col-md-3 d-grid">
            <button
              className="btn btn-primary"
              onClick={searchEmployee}
              disabled={loading}
            >
              {loading ? "Loading…" : "Search"}
            </button>
          </div>

          <div className="col-md-3 d-grid">
            <button
              className="btn btn-secondary"
              onClick={loadAll}
              disabled={loading}
            >
              {loading ? "Loading…" : "Search All"}
            </button>
          </div>
        </div>
      </div>

      {/* Row 2: Employee Code + Email + Save/Update */}
      <div className="card p-3 mb-4 shadow-sm">
        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label">Employee Code</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter Employee Code"
              value={employeeCode}
              onChange={(e) => setEmployeeCode(e.target.value)}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Email ID</label>
            <input
              type="email"
              className="form-control"
              placeholder="Enter Email ID"
              value={emailID}
              onChange={(e) => setEmailID(e.target.value)}
            />
          </div>

          <div className="col-md-4 d-grid">
            <button className="btn btn-success" onClick={addEntry}>
              Save / Update
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead className="table-light">
            <tr>
              <th>ID</th>
              <th>Employee Code</th>
              <th>Email ID</th>
              <th>Plant</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            {rights.map((r) => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td>{r.employeeCode}</td>
                <td>{r.emailID}</td>
                <td>{r.plantName ?? "-"}</td>
                <td>
                  <Switch
                    checked={r.assign}
                    onChange={() => toggleRight(r.id)}
                    onColor="#22c55e"
                    offColor="#9ca3af"
                    height={18}
                    width={36}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save Button */}
      {rights.length > 0 && (
        <div className="mt-3 text-end">
          <button
            className="btn btn-primary"
            onClick={saveRights}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Rights"}
          </button>
        </div>
      )}
    </div>
  );
}

export default EmailRights;
